import { defineBoot } from "#q-app"
import { setupCache } from "axios-cache-interceptor"
import axios from "axios"
import { globalTenantIdRef, globalTenantRecursiveRef } from 'src/stores/tenant.js'

// Get the base URL for this instance. It is supplied by an environment variable.
const baseURL = import.meta.env.REST_API_URL

// The main axios instance
const api = axios.create({
	baseURL: baseURL,
	// 1800 seconds timeout to allow slow requests to complete.
	// This can happen if we try to do an executive PDF report for Past Month on a weak instance.
	timeout: 1800000,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
	useTenant: true,
})

// Default is ['get', 'head']
const cacheMethods = ['get']

// This is the global cache for all API calls
// It is NOT used by default as the request interceptor below sets individual requests to false
// If you want to use it, just set config.cache to true in the request
setupCache(api, {
	methods: cacheMethods,
	ttl: 1000 * 60 * 5, // 5 minutes
	override: true,
})

// Let's add an intercepter that will check if we fail with 401 status any
// of our API calls. If that's the case it will redirect us to the login page.
api.interceptors.response.use(
	(response) => {
		return response
	},
	function (error) {
		if (error.response?.status === 401) {
			if (error.response?.config?.redirect === false) {
				// Let the caller handle the 401 (e.g. isAuthenticated check).
			} else if (window.location.pathname === '/login') {
				// Already on the login page; swallow the 401 silently.
				return new Promise(() => {})
			} else {
				let newLocation = "/login"

				let desiredUrl = window.location.pathname + window.location.search
				if (desiredUrl !== '/') {
					newLocation += "?redirect=" + encodeURIComponent(desiredUrl)
				}
				window.location = newLocation
				return new Promise(() => {})
			}
		}
		return Promise.reject(error)
	}
)

// Apply the root tenant ID to all requests.
api.interceptors.request.use(
	(config) => {
		if (config.useTenant) {
			let tenantId = globalTenantIdRef()
			let recursive = globalTenantRecursiveRef()

			let newParams = {
				rootSegmentId: tenantId.value,
			}
			if (!recursive.value) {
				newParams.segmentId = tenantId.value
			}

			config.params = {
				...newParams,
				...config.params,
			}

			let includeChildSegments = null
			if (Object.hasOwn(config.params, 'includeChildSegments')) {
				includeChildSegments = !!config.params['includeChildSegments']
				delete config.params['includeChildSegments']
			}
			if (Object.hasOwn(config.params, 'segmentIds')) {
				if (Array.isArray(config.params['segmentIds']) && config.params['segmentIds'].length > 0) {
					let segmentID = config.params['segmentIds'][0]
					config.params['rootSegmentId'] = segmentID
					if (includeChildSegments === false) {
						config.params['segmentId'] = segmentID
					} else {
						delete config.params['segmentId']
					}
				}
				delete config.params['segmentIds']
			}
		}
		return config
	}
)

api.interceptors.request.use(
	(config) => {
		if (config.useCache === true) {
			config.cache.override = false
		}
		return config
	}
)

// Default body-less POST/PUT/PATCH requests to an empty {} payload.
//
// Axios 1.x's XHR adapter explicitly strips the Content-Type header
// when the request body is undefined - see lib/adapters/xhr.js's
// "Remove Content-Type if data is undefined" line. Our customer-facing
// REST API is registered as `Consumes(restful.MIME_JSON)`, so a POST
// with the Content-Type stripped is rejected with 415 at the routing
// layer before our handlers ever run. This bites every body-less
// call (e.g. POST /v1/deepdarkweb/{id}/reveal-secret, /acknowledge)
// because none of those endpoints take a request body - they're
// action endpoints whose state lives entirely in the URL.
//
// Defaulting `config.data = {}` here keeps the header alive without
// asking every caller to remember to pass an empty object. We only
// touch genuinely-undefined payloads so explicit `null`/`""`/`0` /
// false bodies pass through unchanged for the rare caller that wants
// them (none in the codebase today, but the contract is preserved).
api.interceptors.request.use(
	(config) => {
		const writeMethods = ["post", "put", "patch"]
		const method = (config.method || "").toLowerCase()
		if (writeMethods.includes(method) && config.data === undefined) {
			config.data = {}
		}
		return config
	}
)

export default defineBoot(({ app }) => {
	// for use inside Vue files (Options API) through this.$axios and this.$api

	app.config.globalProperties.$axios = axios
	// ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
	//       so you won't necessarily have to import axios in each vue file

	app.config.globalProperties.$api = api
	// ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
	//       so you can easily perform requests against your app's API
})

// Export all of our variables so we can use them throughout the app
export { axios, api, baseURL }
